import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import {
    alongEdge,
    BRAND,
    CHART_INK,
    DOMAIN_STATE_COLOR,
    DOMAIN_TRACK,
    flatTopHexCorners,
    READING_PILL
} from './agencyChartTheme';
import {
    CaseloadReading,
    DOMAIN_LABEL,
    DOMAIN_ORDER,
    FULL_SCORE,
    IClientWellbeing,
    READING_LABEL,
    READING_SENTENCE_LABEL
} from '@innerhive-internal/innerhive-common';

const CENTRE = { x: 160, y: 120 };
/** The dashed circle: what this person usually holds. A wedge reaching it is a domain at a hundred. */
const OUTER_R = 90;
const INNER_R = 46;
// Wide enough for "Connection" to sit outside the circle without clipping, which is what sets it.
const WIDTH = 320;
const HEIGHT = 250;
/** How much of each hexagon edge is given up to the gap between wedges, at each end. */
const GAP = 0.05;
const LABEL_OFFSET = 16;
/** Even a zero keeps a sliver, so a wedge never vanishes and leave a gap that reads as missing. */
const MIN_REACH = 0.08;

const useStyles = createUseStyles({
    figure: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        gap: 8,
        margin: 0,
        minWidth: 0,
        width: '100%'
    },

    svg: {
        display: 'block',
        width: '100%',
        height: 'auto'
    },

    domainLabel: {
        fontSize: 11,
        fill: CHART_INK.label
    },

    score: {
        fontSize: 34,
        fontWeight: 600,
        fill: BRAND.deepTeal
    },

    outOf: {
        fontSize: 10,
        fill: CHART_INK.label
    },

    pill: {
        display: 'inline-block',
        padding: '3px 12px',
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase' as const
    }

});

const at = (angleDegrees: number, radius: number) => {
    const radians = (angleDegrees * Math.PI) / 180;
    return {
        x: Math.round((CENTRE.x + radius * Math.cos(radians)) * 100) / 100,
        y: Math.round((CENTRE.y + radius * Math.sin(radians)) * 100) / 100
    };
};

const angleOf = (point: { x: number; y: number }): number =>
    (Math.atan2(point.y - CENTRE.y, point.x - CENTRE.x) * 180) / Math.PI;

/**
 * One wedge: out from a hexagon edge, widening, with a curved outer end.
 *
 * The straight inner edge is the hexagon side; the two flanks run radially outward, so the wedge
 * widens as it goes; the far end is an arc of the circle it reaches. That is what makes six of them
 * read as one round diagram rather than six unrelated darts.
 */
const wedgePath = (params: { fromPoint: { x: number; y: number }; toPoint: { x: number; y: number }; reach: number }): string => {
    const { fromPoint, toPoint, reach } = params;
    const startAngle = angleOf(fromPoint);
    const endAngle = angleOf(toPoint);
    const outerFrom = at(startAngle, reach);
    const outerTo = at(endAngle, reach);

    // Each edge subtends sixty degrees, so the arc is never the long way round.
    return [
        `M ${fromPoint.x},${fromPoint.y}`,
        `L ${outerFrom.x},${outerFrom.y}`,
        `A ${reach} ${reach} 0 0 1 ${outerTo.x},${outerTo.y}`,
        `L ${toPoint.x},${toPoint.y}`,
        'Z'
    ].join(' ');
};

/**
 * One person's recent weeks as six wedges flowering out of a hexagon.
 *
 * The hexagon is the care map's own shape, turned flat-top so its six EDGES face exactly the six
 * directions the labels need. Each wedge grows out from one edge toward a dashed circle that marks
 * what is usual for that person: reaching the circle means nothing has been raised, and falling short
 * means something has.
 *
 * Three things say the same thing on purpose, so none of them has to be read alone: how far a wedge
 * reaches, which of the three status colours fills it, and the number in the middle. A reader who
 * cannot separate the yellow from the green still has the geometry, and a grey printout still has both.
 *
 * Three colours rather than a gradient. A continuous ramp made every wedge a slightly different hue
 * and the reader had to compare shades to compare people, which is exactly the work the bands exist
 * to remove.
 *
 * The number is a way of ordering a caseload, not a measurement of a person. It counts what has been
 * written down recently, so a hundred means nothing has been raised, which is not the same as
 * somebody having checked. The card around it says so.
 */
export const ClientWellbeingHex: FC<{
    wellbeing: IClientWellbeing;
    /** The client's reading, on the pill under the diagram: the one the caseload map draws them in. */
    reading: CaseloadReading;
    /** Drawn larger for the detail view. The geometry and every value are identical. */
    emphasis?: boolean;
}> = ({ wellbeing, reading, emphasis = false }) => {
    const classes = useStyles();

    const corners = flatTopHexCorners({ cx: CENTRE.x, cy: CENTRE.y, radius: INNER_R });
    // Unscored draws only the tracks: a coloured wedge would claim a reading against their own baseline.
    const isScored = wellbeing.overall !== 'unscored';

    return (
        <figure className={classes.figure}>
            {/* group, not img: role="img" would collapse the whole diagram into one label, and each
                wedge carries its own. The client's name is deliberately absent from the label: an
                attribute is recorded verbatim by session replay and no class can mask one, and the
                name is already beside the diagram in an element that can carry mp-mask. */}
            <svg
                className={classes.svg}
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                role="group"
                aria-label={isScored
                    ? `${wellbeing.score} out of ${FULL_SCORE}. ${READING_LABEL[reading]}.`
                    : READING_LABEL.unknown}
                style={{ maxWidth: emphasis ? 420 : 300 }}
            >
                {DOMAIN_ORDER.map((domain, index) => {
                    // By domain rather than by position: the two happen to agree today only because
                    // the readings are built from this same order. Skipping a domain that has no
                    // reading leaves a gap in the diagram, which beats an assertion that would take
                    // the whole tab down inside a render if one were ever omitted.
                    const domainReading = wellbeing.readings.find(candidate => candidate.domain === domain);
                    if (!domainReading) {
                        return null;
                    }

                    const next = (index + 1) % 6;

                    const fromPoint = alongEdge(corners[index], corners[next], GAP);
                    const toPoint = alongEdge(corners[index], corners[next], 1 - GAP);

                    const share = Math.max(MIN_REACH, domainReading.score / FULL_SCORE);
                    const reach = INNER_R + (OUTER_R - INNER_R) * share;

                    const midAngle = angleOf(alongEdge(corners[index], corners[next], 0.5));
                    const labelAt = at(midAngle, OUTER_R + LABEL_OFFSET);
                    const anchor = Math.abs(labelAt.x - CENTRE.x) < 2
                        ? 'middle'
                        : (labelAt.x > CENTRE.x ? 'start' : 'end');

                    return (
                        <g key={domain} data-id="wellbeing-wedge" data-domain={domain} data-state={isScored ? domainReading.state : 'unscored'}>
                            <title>
                                {isScored
                                    ? `${DOMAIN_LABEL[domain]}: ${domainReading.score} out of ${FULL_SCORE}`
                                    : `${DOMAIN_LABEL[domain]}: ${READING_SENTENCE_LABEL.unknown}`}
                            </title>

                            {/* What they usually hold, drawn first, so the shortfall is a visible gap
                                rather than something the reader has to infer from a short wedge. */}
                            <path
                                d={wedgePath({ fromPoint, toPoint, reach: OUTER_R })}
                                fill={DOMAIN_TRACK}
                                fillOpacity={0.45}
                            />

                            {isScored && (
                                <path
                                    d={wedgePath({ fromPoint, toPoint, reach })}
                                    fill={DOMAIN_STATE_COLOR[domainReading.state]}
                                />
                            )}

                            <text
                                className={classes.domainLabel}
                                x={labelAt.x}
                                y={labelAt.y + 4}
                                textAnchor={anchor}
                            >
                                {DOMAIN_LABEL[domain]}
                            </text>
                        </g>
                    );
                })}

                {/* The baseline: everything is measured against reaching this. Drawn over the wedges
                    so it crops them into a compact circle rather than sitting behind them. */}
                <circle
                    cx={CENTRE.x}
                    cy={CENTRE.y}
                    r={OUTER_R}
                    fill="none"
                    stroke={CHART_INK.axis}
                    strokeWidth={1.5}
                    strokeDasharray="5 4"
                />

                <polygon
                    points={corners.map(corner => `${corner.x},${corner.y}`).join(' ')}
                    fill={CHART_INK.surface}
                    stroke={BRAND.tealEdge}
                    strokeWidth={1}
                />
                {isScored && (
                    <>
                        <text className={classes.score} x={CENTRE.x} y={CENTRE.y + 6} textAnchor="middle">
                            {wellbeing.score}
                        </text>
                        <text className={classes.outOf} x={CENTRE.x} y={CENTRE.y + 21} textAnchor="middle">
                            out of {FULL_SCORE}
                        </text>
                    </>
                )}
            </svg>

            {/* The client's reading, the same one their cell on the caseload map is rimmed in, so
                the diagram never says one thing about a person and the map another. */}
            <span
                className={classes.pill}
                data-id="wellbeing-pill"
                data-reading={reading}
                style={READING_PILL[reading]}
            >
                {READING_LABEL[reading]}
            </span>
        </figure>
    );
};
